import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';

import Grid from "@mui/material/Grid2";
import Select from 'react-select';
import { getInputProps } from 'pages/DashboardHelpers';
import { getModelHierarchy, updateModelHierarchy } from 'actions/userActions';

const formControlStyles = { display: 'block', width: '100%', marginBottom: '14px' };
const formFieldStyles = { control: (provided) => ({ ...provided, padding: '6px', borderRadius: "12px" }) };

const toOptions = (arr = []) => arr.map((s) => ({ label: s, value: s }));

const FormField = ({
  label, value, onChange = undefined, options, placeholder,
  isSelect = false, disabled = false, onMenuOpen
}) => (
  <Grid container spacing={2} alignItems="center" sx={formControlStyles}>
    <Grid sx={{ width: '200px', flexShrink: 0 }}>
      <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>{label}:</Typography>
    </Grid>
    <Grid sx={{ flex: 1 }}>
      {isSelect ? (
        <FormControl fullWidth>
          <Select
            styles={formFieldStyles}
            placeholder={placeholder}
            value={value}
            options={options}
            onChange={onChange}
            menuPosition="fixed"
            isClearable={false}
            isDisabled={disabled}
            onMenuOpen={onMenuOpen}
          />
        </FormControl>
      ) : (
        <TextField
          fullWidth
          variant="outlined"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          slotProps={{ input: getInputProps() }}
          disabled={disabled}
        />
      )}
    </Grid>
  </Grid>
);

const buildForm = (row) => ({
  kmatId: row?.kmatId || '',
  modelName: row?.modelName || '',
  region: row?.region || '',
  category: row?.category ? { label: row.category, value: row.category } : null,
  series: row?.series ? { label: row.series, value: row.series } : null,
  subCategory: row?.subCategory ? { label: row.subCategory, value: row.subCategory } : null,
});

export const UpdateModelHrcyDialog = ({ open, onClose, selectedRow, email }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(() => buildForm(null));
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [affectedCount, setAffectedCount] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { kmatId, modelName, region, category, series, subCategory } = form;

  useEffect(() => {
    if (selectedRow && open) {
      let isCancelled = false;
      setForm(buildForm(selectedRow));
      setAffectedCount(null);
      const fetchOptions = async () => {
        if (selectedRow.category) {
          const data = await dispatch(getModelHierarchy({ category: selectedRow.category, series: '' }));
          if (isCancelled) return;
          setSeriesOptions(toOptions(data?.selectedSeries));
          if (selectedRow.series) {
            const subData = await dispatch(getModelHierarchy({ category: selectedRow.category, series: selectedRow.series }));
            if (isCancelled) return;
            setSubCategoryOptions(toOptions(subData?.selectedSubCategory));
          } else {
            setSubCategoryOptions(toOptions(data?.selectedSubCategory));
          }
        }
      };
      fetchOptions();
      return () => { isCancelled = true; };
    }
  }, [selectedRow, open, dispatch]);

  const handleSeriesMenuOpen = async () => {
    if (category) {
      const data = await dispatch(getModelHierarchy({ category: category.value, series: '' }));
      setSeriesOptions(toOptions(data?.selectedSeries));
    }
  };

  const handleSeriesChange = async (selected) => {
    setForm((prev) => ({ ...prev, series: selected, subCategory: null }));
    if (category && selected) {
      const data = await dispatch(getModelHierarchy({ category: category.value, series: selected.value, old_series: selectedRow?.series, kmat_id: selectedRow?.kmatId, region: selectedRow?.region }));
      setSubCategoryOptions(toOptions(data?.selectedSubCategory));
      setAffectedCount(data?.affectedConfigIdsCount || 0);
    } else {
      setSubCategoryOptions([]);
    }
  };

  const handleSubCategoryMenuOpen = async () => {
    if (category && series) {
      const data = await dispatch(getModelHierarchy({ category: category.value, series: series.value }));
      setSubCategoryOptions(toOptions(data?.selectedSubCategory));
    }
  };

  const handleSubCategoryChange = (selected) => {
    setForm((prev) => ({ ...prev, subCategory: selected }));
  };

  const isUpdateDisabled = !region || !kmatId || !category || !series || !modelName || !subCategory;

  const handleUpdate = async () => {
    setIsSubmitting(true);
    await dispatch(updateModelHierarchy(kmatId, region, series?.value, modelName, subCategory?.value, email));
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} className='custom-dialog' fullWidth maxWidth="sm">
      <DialogTitle variant='h6'>Update Model Hierarchy</DialogTitle>
      <DialogContent>
        <FormField label="Region" value={region} placeholder="Select Region" disabled />
        <FormField label="KMAT ID" value={kmatId} placeholder="Enter KMAT ID" disabled />
        <FormField label="Category" value={category} options={category ? [category] : []} placeholder="Select Category" isSelect disabled />
        <FormField label="Series" value={series} onChange={handleSeriesChange} options={seriesOptions} placeholder="Select Series" isSelect onMenuOpen={handleSeriesMenuOpen} />
        <FormField label="Model Name" value={modelName} onChange={(e) => setForm((prev) => ({ ...prev, modelName: e.target.value }))} placeholder="Enter Model Name" />
        <FormField label="Sub Category" value={subCategory} onChange={handleSubCategoryChange} options={subCategoryOptions} placeholder="Select Sub Category" isSelect onMenuOpen={handleSubCategoryMenuOpen} />
        {affectedCount !== null && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Updating this hierarchy will affect <strong>{affectedCount}</strong> existing configuration{affectedCount === 1 ? '' : 's'}.
          </Alert>
        )}
      </DialogContent>
      <DialogActions className='dialog-actions'>
        <Button variant="outlined" className='cancel-button' onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleUpdate} className='add-Button' disabled={isUpdateDisabled || isSubmitting}>Update</Button>
      </DialogActions>
    </Dialog>
  );
};